import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ImageBackground,
  TouchableOpacity,
  Linking,
  Dimensions,
  Platform,
  BackHandler,
} from 'react-native';
import DeviceInfo from 'react-native-device-info';
import { COLORS } from '../../theme/themeTokens';
import type { V2AppBannerResponse, V2BannerDisplayMode } from '../../types/v2';

const appVersion = DeviceInfo.getVersion();

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get('window');

interface BannerScreenProps {
  banner: V2AppBannerResponse;
  onDismiss: () => void;
}

/**
 * BannerScreen — full-screen overlay with banner image, title, version info
 * and a store link.
 *
 * - display_mode = 'show_info'      → user can dismiss and continue into the app
 * - display_mode = 'show_and_block' → user can only close the app
 */
export const BannerScreen: React.FC<BannerScreenProps> = ({ banner, onDismiss }) => {
  const isBlocking = banner.display_mode === 'show_and_block';

  const handlePress = () => {
    if (isBlocking) {
      if (Platform.OS === 'android') {
        BackHandler.exitApp();
      } else {
        // iOS doesn't allow programmatic app exit.
        // Redirect to the store if available, otherwise do nothing.
        if (banner.store_url) {
          Linking.openURL(banner.store_url).catch(() => {});
        }
      }
    } else {
      // Dismiss and continue into the app
      onDismiss();
    }
  };

  const handleStoreLink = () => {
    if (banner.store_url) {
      Linking.openURL(banner.store_url).catch(() => {});
    }
  };

  const content = (
    <View style={styles.overlay}>
      {/* Top content area */}
      <View style={styles.topContent}>
        {/* Title */}
        {banner.title ? (
          <Text style={styles.title}>{banner.title}</Text>
        ) : null}

        {/* Description */}
        {banner.description ? (
          <Text style={styles.description}>{banner.description}</Text>
        ) : null}

        {/* Version info */}
        <View style={styles.versionContainer}>
          {banner.current_version ? (
            <Text style={styles.versionText}>
              New version: {banner.current_version}
            </Text>
          ) : null}
          {banner.min_required_version ? (
            <Text style={styles.versionText}>
              Current version: {appVersion}
            </Text>
          ) : null}
        </View>

        {/* Dismiss / Continue button — only for show_info mode */}
        {!isBlocking ? (
          <TouchableOpacity style={styles.dismissButton} onPress={handlePress}>
            <Text style={styles.dismissButtonText}>Continue</Text>
          </TouchableOpacity>
        ) : null}

        {/* Store link — only shown when store_url is provided */}
        {banner.store_url ? (
          <TouchableOpacity style={[styles.storeButton, { marginTop: 12 }]} onPress={handleStoreLink}>
            <Text style={styles.storeButtonText}>
              {Platform.OS === 'ios' ? 'Open App Store' : 'Open Google Play'}
            </Text>
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  );

  // If we have an image, use it as full-screen background
  if (banner.image_url) {
    return (
      <ImageBackground
        source={{ uri: banner.image_url }}
        style={styles.background}
        resizeMode="cover"
      >
        {content}
      </ImageBackground>
    );
  }

  // Fallback when there is no image
  return <View style={styles.fallbackBackground}>{content}</View>;
};

const styles = StyleSheet.create({
  background: {
    width: SCREEN_WIDTH,
    height: SCREEN_HEIGHT,
  },
  fallbackBackground: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  overlay: {
    flex: 1,
    
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: Platform.OS === 'ios' ? 80 : 60,
    paddingBottom: Platform.OS === 'ios' ? 100 : 80,
  },
  topContent: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: COLORS.white,
    textAlign: 'center',
    marginBottom: 12,
  },
  description: {
    fontSize: 18,
    fontWeight: '400',
    color: 'rgba(255,255,255,0.85)',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: 22,
  },
  versionContainer: {
    alignItems: 'center',
    marginBottom: 24,
  },
  versionText: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.8)',
    marginBottom: 4,
  },
  storeButton: {
    backgroundColor: COLORS.brandOrange,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 32,
    marginBottom: 12,
    width: '100%',
    alignItems: 'center',
  },
  storeButtonText: {
    fontFamily: 'Inter',
    lineHeight: 22,
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
  },
  dismissButton: {
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.6)',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 32,
    width: '100%',
    alignItems: 'center',
  },
  dismissButtonText: {
    fontFamily: 'Inter',
    lineHeight: 22,
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.white,
  },
});

export default BannerScreen;
